import { Badge } from '@givebutter/primitives/badge';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import * as ButtonGroup from '@givebutter/primitives/button-group';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { maskWholeNumber } from '@givebutter/util';
import { faEnvelope, faPenNib, faUsersSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PersonalizedContactButton } from 'assets/js/pages/engage/letters/PersonalizedContactButton';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { usePersonalizedLetterContactsQuery } from 'assets/js/pages/engage/letters/usePersonalizedLetterContactsQuery';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import { PersonalizedLettersFilter } from 'assets/js/pages/engage/types';
import * as Input from 'components/input';

const PersonalizedLetterRecipientsList = () => {
    const { data: letter } = useCurrentLetter();
    const {
        personalizedLettersContactTab,
        setPersonalizedLettersContactTab,
        personalizedLetterSearch: search,
        setPersonalizedLetterSearch: setSearch,
    } = usePersonalizedLettersStore();

    const { data, hasNextPage, fetchNextPage, isPending } = usePersonalizedLetterContactsQuery();
    const contacts = data.pages;
    const isEmpty = contacts.length === 0;

    return (
        <div className={'flex flex-col bg-white'}>
            <div className={'flex flex-col gap-4 border-b border-b-gray-200 bg-white p-4 lg:p-8'}>
                <div className={'flex items-center gap-2'}>
                    <p className={'text-xl font-medium text-gray-900'}>Review letters</p>
                </div>
                <p className={'text-gray-600'}>
                    Ensure that the content is correct and, if desired, tailor letters to meet the
                    needs of each recipient.
                </p>
                <Input.Root size={'sm'}>
                    <Input.SearchIcon />
                    <Input.Form
                        placeholder={'Search'}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                </Input.Root>
                <ButtonGroup.Root
                    type="single"
                    value={personalizedLettersContactTab}
                    onValueChange={(value: PersonalizedLettersFilter) =>
                        setPersonalizedLettersContactTab(value)
                    }
                >
                    <ButtonGroup.Item value={'all'}>
                        <div className={'flex items-center justify-center gap-2'}>
                            All
                            <Badge
                                size="sm"
                                className="group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200"
                            >
                                {maskWholeNumber(letter.mail_piece_counts?.total)}
                            </Badge>
                        </div>
                    </ButtonGroup.Item>
                    <ButtonGroup.Item value={'personalized'}>
                        <div className={'flex items-center justify-center gap-2'}>
                            Personalized
                            <Badge
                                size="sm"
                                className="group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200"
                            >
                                {maskWholeNumber(letter.mail_piece_counts?.personalized)}
                            </Badge>
                        </div>
                    </ButtonGroup.Item>
                    <ButtonGroup.Item value={'removed'}>
                        <div className={'flex items-center justify-center gap-2'}>
                            Excluded
                            <Badge
                                size="sm"
                                className="group-radix-state-on:bg-blue-50 group-radix-state-on:text-blue-600 bg-gray-200"
                            >
                                {maskWholeNumber(letter.mail_piece_counts?.excluded)}
                            </Badge>
                        </div>
                    </ButtonGroup.Item>
                </ButtonGroup.Root>
            </div>
            {isEmpty ? (
                <EmptyRecipientsList />
            ) : (
                <LoadingOverlay
                    isLoading={isPending}
                    className={'flex flex-grow flex-col overflow-y-auto pb-32'}
                >
                    {contacts.map(contact => (
                        <PersonalizedContactButton key={contact.id} contact={contact} />
                    ))}
                    {hasNextPage && (
                        <Button
                            variant="transparent"
                            onClick={() => fetchNextPage()}
                            className="mt-4"
                        >
                            Load more
                        </Button>
                    )}
                </LoadingOverlay>
            )}
        </div>
    );
};

const EmptyRecipientsList = () => {
    const { personalizedLettersContactTab } = usePersonalizedLettersStore();
    const { icon, text, subText } = getEmptyPropsFromRecipientTab(personalizedLettersContactTab);

    return (
        <div className={'flex flex-col items-center gap-4 p-12 text-center'}>
            <BoxIcon size="lg" variant="secondary">
                <FontAwesomeIcon icon={icon} />
            </BoxIcon>
            <div className={'flex flex-col gap-2'}>
                <p className={'font-medium text-gray-600'}>{text}</p>
                <p className={'text-sm text-gray-600'}>{subText}</p>
            </div>
        </div>
    );
};

const getEmptyPropsFromRecipientTab = (tab: PersonalizedLettersFilter) => {
    let icon = faEnvelope;
    let text = 'No recipients found';
    let subText = 'All letters for selected recipients will be displayed here.';

    switch (tab) {
        case 'personalized':
            icon = faPenNib;
            text = 'No personalized letters';
            subText =
                'Letters that have been personalized for an individual recipient will be displayed here.';
            break;
        case 'removed':
            icon = faUsersSlash;
            text = 'No excluded recipients';
            subText = 'Recipients who have been manually excluded will be displayed here.';
            break;
        case 'all':
        default:
            break;
    }

    return {
        icon,
        text,
        subText,
    };
};

export { PersonalizedLetterRecipientsList };
